import { Modal } from "react-bootstrap";
import { useContext, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import { toast } from "../../helper/common";

const BillingCustomization = ({ onClose }) => {
  const { _t } = useTranslator();
  const { updateWorkspaceBilling } = useContext(LandLordContext);
  const { user, loadUserTenant } = useContext(AuthContext);

  const saved = user?.current_workspace?.billing_details ?? {};

  const snapshot = {
    billing_type: saved.billing_type || "business",
    company_name: saved.company_name || "",
    full_name: saved.full_name || "",
    tax_id: saved.tax_id || "",
    country: saved.country || "",
    city: saved.city || "",
    address: saved.address || "",
    postal_code: saved.postal_code || "",
  };

  const [billingType, setBillingType] = useState(snapshot.billing_type);
  const [billingData, setBillingData] = useState({
    company_name: snapshot.company_name,
    full_name: snapshot.full_name,
    tax_id: snapshot.tax_id,
    country: snapshot.country,
    city: snapshot.city,
    address: snapshot.address,
    postal_code: snapshot.postal_code,
  });
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) =>
    setBillingData((d) => ({ ...d, [key]: e.target.value }));

  const numericOnlyOnKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Enter",
    ];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  };

  const {
    company_name,
    full_name,
    tax_id,
    country,
    city,
    address,
    postal_code,
  } = billingData;

  const postalCodeValid =
    postal_code.trim() === "" || postal_code.trim().length === 5;

  const baseComplete =
    [country, city, address].every((v) => v.trim() !== "") &&
    postal_code.trim().length === 5;
  const isComplete =
    billingType === "business"
      ? baseComplete && company_name.trim() !== "" && tax_id.trim() !== ""
      : baseComplete && full_name.trim() !== "";

  const hasChanges =
    billingType !== snapshot.billing_type ||
    company_name !== snapshot.company_name ||
    full_name !== snapshot.full_name ||
    tax_id !== snapshot.tax_id ||
    country !== snapshot.country ||
    city !== snapshot.city ||
    address !== snapshot.address ||
    postal_code !== snapshot.postal_code;

  const canSave = isComplete && hasChanges && !saving;

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateWorkspaceBilling({
        billing_type: billingType,
        ...(billingType === "business"
          ? { company_name, tax_id }
          : { full_name }),
        country,
        city,
        address,
        postal_code,
      });
      await loadUserTenant("landlord");
      toast.success(_t("billingDetailsSaved"));
      onClose?.();
    } catch (err) {
      console.log(err);
      toast.error(_t("errorWhileSavingBillingDetails"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal.Body className="billing-tab">
      <div className="billing-tab__header">
        <h4 className="billing-tab__title">{_t("billingDetails")}</h4>
        <div className="billing-tab__type-toggle">
          <button
            type="button"
            className={`billing-tab__type-opt${billingType === "private" ? " billing-tab__type-opt--active" : ""}`}
            onClick={() => setBillingType("private")}
          >
            {_t("private")}
          </button>
          <button
            type="button"
            className={`billing-tab__type-opt${billingType === "business" ? " billing-tab__type-opt--active" : ""}`}
            onClick={() => setBillingType("business")}
          >
            {_t("business")}
          </button>
        </div>
      </div>
      <p className="company-name-text modal-margin-bottom margin-y">
        {_t("billingDetailsDescription")}
      </p>

      <div className="billing-tab__grid">
        {billingType === "business" ? (
          <>
            <div className="forminput customInput mb-0">
              <label>{_t("companyName")}</label>
              <input
                className="modal-input"
                type="text"
                value={company_name}
                onChange={setField("company_name")}
                placeholder={_t("enterCompanyName")}
              />
            </div>
            <div className="forminput customInput mb-0">
              <label>{_t("taxId")}</label>
              <input
                className="modal-input"
                type="text"
                value={tax_id}
                onChange={setField("tax_id")}
                placeholder={_t("enterTaxId")}
              />
            </div>
          </>
        ) : (
          <div className="forminput customInput mb-0 col-span-2">
            <label>{_t("fullName")}</label>
            <input
              className="modal-input"
              type="text"
              value={full_name}
              onChange={setField("full_name")}
              placeholder={_t("enterFullName")}
            />
          </div>
        )}

        <div className="forminput customInput mb-0">
          <label>{_t("country")}</label>
          <input
            className="modal-input"
            type="text"
            value={country}
            onChange={setField("country")}
            placeholder={_t("enterCountry")}
          />
        </div>

        <div className="forminput customInput mb-0">
          <label>{_t("city")}</label>
          <input
            className="modal-input"
            type="text"
            value={city}
            onChange={setField("city")}
            placeholder={_t("enterCity")}
          />
        </div>

        <div className="forminput customInput mb-0">
          <label>{_t("address")}</label>
          <input
            className="modal-input"
            type="text"
            value={address}
            onChange={setField("address")}
            placeholder={_t("enterAddress")}
          />
        </div>

        <div className="forminput customInput mb-0">
          <label>{_t("postalCode")}</label>
          <input
            className={`modal-input${!postalCodeValid ? " modal-input--error" : ""}`}
            type="text"
            value={postal_code}
            onChange={setField("postal_code")}
            onKeyDown={numericOnlyOnKeyDown}
            maxLength={5}
            placeholder={_t("enterPostalCode")}
          />
          {!postalCodeValid && (
            <span className="billing-tab__field-error">
              {_t("postalCodeMustBe5Digits")}
            </span>
          )}
        </div>
      </div>

      <div className="billing-tab__footer inviteButtonWrapper">
        <button
          type="button"
          className="btnNext basicInfoButton greenHover font-size-14"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? `${_t("saving")}...` : _t("save")}
        </button>
      </div>
    </Modal.Body>
  );
};

export default BillingCustomization;
